import { Box, CircularProgress, Dialog, DialogContent, Modal, Step, StepConnector, StepLabel, Stepper } from '@mui/material';
import { useEffect, useState } from 'react';

import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';

import queryClient from '../../config/queryClient';
import DeliveryMethodSelector from './DeliveryMethodSelector';
import CustomerSelector from './CustomerSelector';
import SuccessView from './SuccessView';
import { reviewRequestKeys, useCreateReviewRequest } from '../../services/business/reviewRequest/reviewRequest.hooks.js';
import { PHONE_TYPES } from '../../utility/Constant.js';
import styled from '@emotion/styled';
import { toast } from 'sonner';
import { useGetShopConfig } from '../../services/core/settings/settings.hooks.js';
import { useMemo } from 'react';
import { useGetAppointmentForInvoiceByCustomer } from '../../services/business/invoices/invoices.hooks.js';
import { buildReviewLastSentMessage } from '../../services/business/invoices/invoices.utils.js';
import { USER_TIMEZONE } from '../../utility/Constant.js';

const steps = ['Select Customer', 'Choose Delivery Method'];
const CustomConnector = styled(StepConnector)(() => ({
    top: 10,
    left: 'calc(-50% + 22px)',
    right: 'calc(50% + 22px)',

    '& .MuiStepConnector-line': {
        width: '58px',
        minWidth: '58px',
        height: '1px',
        border: 'none',
        backgroundColor: '#C4C4C4',
    },
}));
const styles = {
    overlay: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
    },
    modal: {
        width: '100%',
        maxWidth: '700px',
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        overflow: 'hidden',
        outline: 'none',
        boxShadow: '0px 1px 10px 0px rgba(0, 0, 0, 0.12), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 2px 4px -1px rgba(0, 0, 0, 0.20)',
    },
    stepperWrapper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '417px',
        margin: '0 auto',
        paddingTop: '24px',
        paddingBottom: '7px',
    },
    content: {
        px: 3,
        pt: 2,
        pb: 3,
        maxHeight: '65vh',
        overflowY: 'auto',
    },
    // content: {
    //     paddingLeft: '24px',
    //     paddingRight: '24px',
    //     paddingTop: '20px',
    //     paddingBottom: '24px',
    //     maxHeight: '65vh',
    //     overflowY: 'auto',
    // },
};

const AddQuickReviewModal = ({
    open = false,
    onClose,
    prefetchedQuickReviewUsers = undefined,
    prefetchedCustomersPage1 = undefined,
    prefetchedCustomersPage1Meta = undefined,
}) => {
    const [activeStep, setActiveStep] = useState(0);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [deliveryMethod, setDeliveryMethod] = useState([]);
    const [phoneTypes, setPhoneTypes] = useState(PHONE_TYPES);
    const [selectedPhoneTypes, setSelectedPhoneTypes] = useState({
        primary: 'mobile',
        secondary: 'mobile',
    });

    // ADD state near the other states:
    const [phoneMatchedCustomer, setPhoneMatchedCustomer] = useState(null);

    // ── NEW CUSTOMER STATE (lifted up so handleSubmit can read it) ──
    const [newCustomer, setNewCustomer] = useState({
        firstName: '',
        phoneNumber: '',
        email: '',
    });

    // ── selectedCountry lifted up from SelectCustomerForm ──
    const [selectedCountry, setSelectedCountry] = useState(null);

    // ── customMessage lifted up from DeliveryMethodForm ──
    const [customMessage, setCustomMessage] = useState('');

    // Persist "Add New Customer" collapse state across step navigation
    const [addCustomerOpen, setAddCustomerOpen] = useState(true);

    // Success screen state
    const [showSuccess, setShowSuccess] = useState(false);
    const [submittedCustomers, setSubmittedCustomers] = useState([]);
    const [submittedNewCustomer, setSubmittedNewCustomer] = useState(null);

    const { data: shopConfigData } = useGetShopConfig();

    // Prefetch "last review sent" info while still on step 0
    const shopId = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('apex-saas_details'))?._id || null;
        } catch {
            return null;
        }
    }, []);

    const existingCustomers = Array.isArray(selectedCustomer) ? selectedCustomer : selectedCustomer ? [selectedCustomer] : [];
    const isExactlyOneExisting = existingCustomers.length === 1 && !!existingCustomers[0]?.id;
    const selectedExistingCustomerId = isExactlyOneExisting ? existingCustomers[0].id : null;

    const appointmentForInvoiceByCustomerQuery = useGetAppointmentForInvoiceByCustomer(
        { shopId, customerId: selectedExistingCustomerId },
        { enabled: isExactlyOneExisting && !!shopId && !!selectedExistingCustomerId }
    );

    const lastSentMessage = useMemo(() => {
        if (!isExactlyOneExisting) return '';
        if (!appointmentForInvoiceByCustomerQuery.data) return '';
        return buildReviewLastSentMessage(appointmentForInvoiceByCustomerQuery.data, USER_TIMEZONE);
    }, [appointmentForInvoiceByCustomerQuery.data, isExactlyOneExisting]);

    const resetForm = () => {
        setActiveStep(0);
        setSelectedCustomer(null);
        setDeliveryMethod([]);
        setNewCustomer({ firstName: '', phoneNumber: '', email: '' });
        setSelectedCountry(null);
        setCustomMessage('');
        setAddCustomerOpen(true);
        setShowSuccess(false);
        setSubmittedCustomers([]);
        setSubmittedNewCustomer(null);
    };

    useEffect(() => {
        if (open) resetForm();
    }, [open]);

    const { mutate: createReviewRequest, isPending } = useCreateReviewRequest({
        onSuccess: (res) => {
            console.log('Success:', res);

            //  Invalidate reviews list
            queryClient.invalidateQueries({
                queryKey: reviewRequestKeys.all, // invalidates all reviews queries
            });

            const selectedArray = Array.isArray(selectedCustomer) ? selectedCustomer : [];
            const isNewCust = selectedArray.length === 0;

            if (isNewCust) {
                setSubmittedNewCustomer({ ...newCustomer });
                setSubmittedCustomers([]);
            } else {
                setSubmittedCustomers([...selectedArray]);
                setSubmittedNewCustomer(null);
            }

            setShowSuccess(true);
        },
    });

    const handleSelectedCustomerChange = (nextSelected) => {
        const nextArray = Array.isArray(nextSelected) ? nextSelected : [];
        if (nextArray.length > 5) {
            toast.error('Limit reached — you can select up to 5 customers per request.');
            return;
        }
        setSelectedCustomer(nextSelected);
    };

    const handleNext = () => {
        const selectedArray = Array.isArray(selectedCustomer) ? selectedCustomer : [];
        const hasSelectedCustomer = selectedArray.length > 0;
        const hasNewCustomerName = newCustomer.firstName?.trim() !== '';
        const hasNewCustomerContact = newCustomer.phoneNumber?.trim() !== '' || newCustomer.email?.trim() !== '';
        const hasNewCustomer = hasNewCustomerName && hasNewCustomerContact;

        if (activeStep === 0) {
            // If user is trying to add a new customer, enforce required contact fields
            if (!hasSelectedCustomer && (hasNewCustomerName || hasNewCustomerContact)) {
                if (!hasNewCustomerContact) {
                    toast.error('Please enter phone number or email address to continue');
                    return;
                }
                if (!hasNewCustomerName) {
                    toast.error('Please enter customer name to continue');
                    return;
                }
            }

            if (!hasSelectedCustomer && !hasNewCustomer) {
                toast.error('Please select a customer to continue');
                return;
            }
        }

        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => setActiveStep((prev) => prev - 1);

    const handleSubmit = () => {
        const selectedArray = Array.isArray(selectedCustomer) ? selectedCustomer : [];
        const customerIds = selectedArray.map((c) => c.id);

        const reviewRequest = {
            shouldSendReviewEmailToCustomer: deliveryMethod.includes('email'),
            shouldSendReviewSMSToCustomer: deliveryMethod.includes('text'),
            shouldSendReviewNotificationToCustomer: deliveryMethod.includes('APP') || deliveryMethod.includes('app'),
            customMessage: customMessage ?? '',
        };

        let payload = {};

        // AFTER
        if (selectedArray.length > 0) {
            const regularCustomerIds = selectedArray.filter((c) => c.createdFrom !== 'shop_imported').map((c) => c.id);

            const importedCustomerIds = selectedArray.filter((c) => c.createdFrom === 'shop_imported').map((c) => c.id);

            payload = {
                customerId: regularCustomerIds, // shop_created + apex_auto_app
                importCustomerIds: importedCustomerIds, // shop_imported
                reviewRequest,
            };
        } else {
            //  New / static customer
            payload = {
                firstName: newCustomer.firstName,

                ...(newCustomer.phoneNumber?.trim()
                    ? {
                          phoneNumber: newCustomer.phoneNumber,
                          phoneNumberCountry: selectedCountry?._id ?? '',
                          countryCode: selectedCountry?.dial_code ?? '',
                      }
                    : {}),

                ...(newCustomer.email?.trim() ? { email: newCustomer.email } : {}),
                reviewRequest,
            };
        }

        createReviewRequest(payload);
    };

    const handleSendAnother = () => resetForm();

    // ADD before the return:
    const selectedArray = Array.isArray(selectedCustomer) ? selectedCustomer : [];
    const hasExistingSelected = selectedArray.length > 0;
    const hasNewCustomerFilled = newCustomer.firstName?.trim() !== '' && (newCustomer.phoneNumber?.trim() !== '' || newCustomer.email?.trim() !== '');

    // NEXT is blocked on step 0 if phone matched but user hasn't resolved it
    const canNext = activeStep !== 0 || (!phoneMatchedCustomer && (hasExistingSelected || hasNewCustomerFilled)) || hasExistingSelected;

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <Box sx={styles.overlay}>
                    <Box sx={styles.modal}>
                        <ModalHeader onClose={onClose} />

                        {showSuccess ? (
                            //  Success Screen
                            <SuccessView
                                customers={submittedCustomers}
                                newCustomer={submittedNewCustomer}
                                deliveryMethod={deliveryMethod}
                                onClose={onClose}
                                onSendAnother={handleSendAnother}
                            />
                        ) : (
                            <>
                                {/*  Stepper  */}
                                <Box sx={styles.stepperWrapper}>
                                    <Stepper
                                        activeStep={activeStep}
                                        connector={<CustomConnector />}
                                        sx={{
                                            maxWidth: '600px',
                                            width: '100%',
                                            height: '48px',
                                            '& .MuiStepLabel-root .MuiStepLabel-iconContainer': {
                                                paddingRight: '8px',
                                            },
                                            // Disabled/Inactive step styling
                                            '& .MuiStep-root:not(.Mui-completed) .MuiStepIcon-root:not(.Mui-active)': {
                                                color: '#9B9C9E', // Gray background for disabled steps
                                                '& .MuiStepIcon-text': {
                                                    backgroundColor: '#9B9C9E',
                                                    fill: '#fff', // White number text
                                                },
                                            },
                                            // Disabled/Inactive step label text
                                            '& .MuiStep-root:not(.Mui-completed) .MuiStepLabel-label:not(.Mui-active)': {
                                                color: '#9B9C9E', // Gray text for disabled steps
                                                fontWeight: 500,
                                                lineHeight: '150%',
                                            },
                                            // Active step styling (blue)
                                            '& .MuiStepIcon-root.Mui-active': {
                                                color: '#1976d2', // Blue background for active step
                                                fontWeight: 500,
                                                lineHeight: '150%',
                                                '& .MuiStepIcon-text': {
                                                    fill: '#fff', // White number text
                                                },
                                            },
                                            // Active step label text
                                            '& .MuiStepLabel-label.Mui-active': {
                                                color: '#212121', // Dark text for active step
                                                fontWeight: 500,
                                                lineHeight: '150%',
                                            },
                                            // Completed step styling (blue with checkmark)
                                            '& .MuiStepIcon-root.Mui-completed': {
                                                color: '#1976d2', // Blue background for completed steps
                                            },
                                            // Completed step label text
                                            '& .MuiStepLabel-label.Mui-completed': {
                                                color: '#212121', // Dark text for completed steps
                                                fontWeight: 500,
                                                lineHeight: '150%',
                                            },
                                            // Connector line styling
                                            '& .MuiStepConnector-line': {
                                                borderColor: '#E0E0E0',
                                            },
                                        }}
                                    >
                                        {steps.map((label) => (
                                            <Step key={label}>
                                                <StepLabel>{label}</StepLabel>
                                            </Step>
                                        ))}
                                    </Stepper>
                                </Box>

                                {/*  Step Content  */}
                                <Box sx={styles.content}>
                                    {activeStep === 0 && (
                                        <CustomerSelector
                                            selectedCustomer={selectedCustomer}
                                            setSelectedCustomer={handleSelectedCustomerChange}
                                            newCustomer={newCustomer}
                                            setNewCustomer={setNewCustomer}
                                            selectedCountry={selectedCountry}
                                            setSelectedCountry={setSelectedCountry}
                                            phoneTypes={phoneTypes}
                                            selectedPhoneTypes={selectedPhoneTypes}
                                            setSelectedPhoneTypes={setSelectedPhoneTypes}
                                            addCustomerOpen={addCustomerOpen}
                                            setAddCustomerOpen={setAddCustomerOpen}
                                            prefetchedQuickReviewUsers={prefetchedQuickReviewUsers}
                                            prefetchedCustomersPage1={prefetchedCustomersPage1}
                                            prefetchedCustomersPage1Meta={prefetchedCustomersPage1Meta}
                                            onFoundCustomerChange={setPhoneMatchedCustomer} // ← ADD
                                        />
                                    )}

                                    {activeStep === 1 && (
                                        <DeliveryMethodSelector
                                            selectedCustomer={selectedCustomer}
                                            setSelectedCustomer={setSelectedCustomer}
                                            deliveryMethod={deliveryMethod}
                                            setDeliveryMethod={setDeliveryMethod}
                                            customMessage={customMessage}
                                            setCustomMessage={setCustomMessage}
                                            newCustomer={newCustomer}
                                            shopConfigData={shopConfigData}
                                            lastSentMessage={lastSentMessage}
                                        />
                                    )}
                                </Box>

                                <ModalFooter
                                    activeStep={activeStep}
                                    steps={steps}
                                    onBack={handleBack}
                                    onNext={handleNext}
                                    onSubmit={handleSubmit}
                                    onClose={onClose}
                                    isLoading={isPending}
                                    canNext={canNext}
                                />
                            </>
                        )}
                    </Box>
                </Box>
            </Modal>

            {/* ── Cards loading overlay ── */}
            <Dialog open={isPending}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AddQuickReviewModal;
